import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CART } from "@/constants/testIds";

export const CartDrawer = () => {
  const { open, closeCart, items, subtotal, setQty, removeItem, checkoutViaWhatsApp } = useCart();
  const { format } = useCurrency();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-forest/40 backdrop-blur-[2px]"
            onClick={closeCart}
          />
          <motion.aside
            data-testid={CART.drawer}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[80] w-full sm:w-[440px] bg-white flex flex-col shadow-2xl"
          >
            {/* header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-hairline">
              <div>
                <div className="overline text-gold">§ Cart</div>
                <div className="font-display text-forest text-2xl leading-none mt-1">
                  Your selection
                </div>
              </div>
              <button
                data-testid={CART.close}
                onClick={closeCart}
                aria-label="Close cart"
                className="p-2 text-forest hover:text-gold transition-colors duration-500"
              >
                <X size={20} strokeWidth={1.4} />
              </button>
            </div>

            {/* items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div data-testid={CART.empty} className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="font-display text-forest text-3xl leading-tight max-w-[16ch] mb-3">
                    Your cart is <span className="font-editorial italic text-champagne">quietly empty.</span>
                  </div>
                  <p className="text-forest/60 max-w-[24ch] mb-6">
                    Add a set of sheets, a duvet or a custom curtain to begin.
                  </p>
                  <Link
                    to="/shop"
                    onClick={closeCart}
                    className="overline text-forest border border-forest rounded-full px-5 py-3 hover:bg-forest hover:text-white transition-colors duration-500"
                  >
                    Browse the Shop →
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-hairline">
                  {items.map((it) => (
                    <li
                      key={`${it.slug}-${it.summary || ""}`}
                      data-testid={CART.item(it.slug)}
                      className="py-4 flex gap-4"
                    >
                      <Link to={`/product/${it.slug}`} onClick={closeCart} className="w-20 h-24 shrink-0 overflow-hidden clip-slab bg-beige">
                        <img src={it.image} alt={it.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-3">
                          <Link to={`/product/${it.slug}`} onClick={closeCart} className="font-display text-forest text-lg leading-tight truncate">
                            {it.title}
                          </Link>
                          <button
                            data-testid={CART.itemRemove(it.slug)}
                            aria-label="Remove"
                            onClick={() => removeItem(it.slug, it.summary)}
                            className="text-forest/50 hover:text-forest transition-colors duration-500"
                          >
                            <Trash2 size={16} strokeWidth={1.4} />
                          </button>
                        </div>
                        <div className="overline text-forest/60 mt-1 whitespace-normal break-words">
                          {it.summary || [it.size, it.colour].filter(Boolean).join(" · ") || "—"}
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="inline-flex items-center border border-hairline rounded-full overflow-hidden">
                            <button
                              data-testid={CART.itemQtyMinus(it.slug)}
                              onClick={() => setQty(it.slug, it.summary, it.qty - 1)}
                              className="px-2.5 py-1.5 text-forest hover:bg-beige/60 transition-colors duration-500"
                              aria-label="Decrease"
                            >
                              <Minus size={12} strokeWidth={1.6} />
                            </button>
                            <span className="px-3 tabnum text-forest text-sm">{it.qty}</span>
                            <button
                              data-testid={CART.itemQtyPlus(it.slug)}
                              onClick={() => setQty(it.slug, it.summary, it.qty + 1)}
                              className="px-2.5 py-1.5 text-forest hover:bg-beige/60 transition-colors duration-500"
                              aria-label="Increase"
                            >
                              <Plus size={12} strokeWidth={1.6} />
                            </button>
                          </div>
                          <div className="font-display text-forest text-lg tabnum">
                            {format(it.price * it.qty)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* footer */}
            {items.length > 0 && (
              <div className="border-t border-hairline px-6 py-5 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="overline text-forest/60">Subtotal</span>
                  <span data-testid={CART.subtotal} className="font-display text-forest text-3xl tabnum">
                    {format(subtotal)}
                  </span>
                </div>
                <Link
                  to="/checkout"
                  onClick={closeCart}
                  data-testid="cart-checkout-card"
                  className="w-full relative overflow-hidden shine inline-flex items-center justify-center gap-3 bg-forest text-white overline px-6 py-4 rounded-full hover:bg-[hsl(150_60%_16%)] transition-colors duration-500"
                >
                  Checkout · Pay by Card or PayPal
                  <span aria-hidden>→</span>
                </Link>
                <button
                  data-testid={CART.checkout}
                  onClick={checkoutViaWhatsApp}
                  className="w-full inline-flex items-center justify-center gap-3 border border-forest/70 text-forest overline px-6 py-3 rounded-full hover:bg-forest hover:text-white transition-colors duration-500"
                >
                  Order via WhatsApp instead
                </button>
                <p className="text-center text-forest/60 text-xs">
                  Cards accepted worldwide · SSL-secured checkout
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
